/**
 * Clear the platform's caches by hand.
 *
 * A promote already flushes these on the way through, but a change made
 * outside a promote — or one that landed just after a promote's flush — has had
 * no way to ask for it short of waiting out a TTL or having someone restart a
 * pod. This asks admin-bff for the same fleet clear the promotion worker does.
 *
 * It reports per service rather than one success flag. A service can fail while
 * the others clear, and on an environment where flex has no cache token that is
 * the normal, permanent state; a single red banner would say "the cache is
 * stale" when five of six caches had just been emptied. The person clicking
 * wants to know WHICH cache is still holding something.
 *
 * Confirmation first: this is a fleet-wide flush that every tenant on the
 * environment feels for the moment it takes the caches to refill.
 */

import React, { useState } from 'react';
import {
  Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Paper, Typography,
} from '@mui/material';
import CachedIcon from '@mui/icons-material/Cached';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { useMutation } from '@tanstack/react-query';
import axiosClient from '../../api/axiosClient';
import { colors, typo, primaryButtonSx, secondaryButtonSx, dialogActionsSx } from '../../theme/tokens';

/** One row of admin-bff's clear-all response. */
interface ServiceResult {
  service: string;
  success: boolean;
  keysRemoved: number;
  message: string | null;
  error: string | null;
}

interface ClearAllResponse {
  environment?: string;
  requestedAtUtc?: string;
  overallSuccess?: boolean;
  totalKeysRemoved?: number;
  results?: ServiceResult[];
}

/**
 * The server answers a refusal as {error, message}; show the message, not the
 * code. The one code with no message of its own is the /api/admin kill switch,
 * which 503s every mutation when ADMIN_VIEW_ENABLED is off — "admin_view_disabled"
 * on screen tells the reader nothing they can act on.
 */
const CODE_TEXT: Record<string, string> = {
  admin_view_disabled:
    'Admin View writes are switched off in this environment (ADMIN_VIEW_ENABLED), so the '
    + 'cache clear was refused before it ran.',
};

function failureText(err: any): string {
  const data = err?.response?.data;
  if (data?.error && CODE_TEXT[data.error]) return CODE_TEXT[data.error];
  if (data?.message) return String(data.message);
  if (data?.error) return String(data.error);
  return err?.message ? String(err.message) : 'The cache clear could not be started.';
}

const ResetCacheCard: React.FC = () => {
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [result, setResult] = useState<ClearAllResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  const clear = useMutation({
    mutationFn: async () => {
      const res = await axiosClient.post<ClearAllResponse>('/api/admin/cache/clear-all');
      return res.data;
    },
    onMutate: () => { setResult(null); setError(null); },
    onSuccess: (data) => setResult(data),
    onError: (err) => setError(failureText(err)),
  });

  const rows = result?.results ?? [];
  const cleared = rows.filter((r) => r.success).length;

  return (
    <Paper
      elevation={0}
      sx={{
        p: 3, mb: 4, borderRadius: '12px',
        border: `1px solid ${colors.borderLight}`, backgroundColor: colors.bgWhite,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
        <CachedIcon sx={{ color: colors.textSecondary, fontSize: 20 }} />
        <Typography sx={{ ...typo.sectionTitle, fontSize: 16, color: colors.textPrimary }}>
          Platform cache
        </Typography>
      </Box>

      <Typography sx={{ ...typo.secondaryText, mb: 2 }}>
        Clears the cohort, tenant, wallet, CMS, Flex and content caches for this environment —
        the same flush a promote performs. Use it when something you changed outside a promote
        is still showing the old value. Every tenant here is served from these caches, so they
        refill from the database for a moment afterwards.
      </Typography>

      <Button
        variant="contained"
        disabled={clear.isPending}
        onClick={() => setConfirmOpen(true)}
        startIcon={clear.isPending ? <CircularProgress size={16} color="inherit" /> : <CachedIcon />}
        sx={primaryButtonSx}
      >
        {clear.isPending ? 'Clearing…' : 'Clear all caches'}
      </Button>

      {error && (
        <Box sx={{ mt: 2, display: 'flex', gap: 1, alignItems: 'flex-start' }}>
          <ErrorOutlineIcon sx={{ color: colors.errorText, fontSize: 18, mt: '1px' }} />
          <Typography sx={{ ...typo.secondaryText, fontSize: 13, color: colors.errorText }}>
            {error}
          </Typography>
        </Box>
      )}

      {result && (
        <Box sx={{ mt: 2.5 }}>
          <Typography sx={{ ...typo.secondaryText, fontSize: 13, mb: 1 }}>
            {`${cleared} of ${rows.length} cleared`}
            {typeof result.totalKeysRemoved === 'number' ? ` · ${result.totalKeysRemoved} keys removed` : ''}
            {result.environment ? ` · ${result.environment}` : ''}
          </Typography>
          <Box sx={{ display: 'grid', gap: 0.75 }}>
            {rows.map((r) => (
              <Box key={r.service} sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                {r.success
                  ? <CheckCircleIcon sx={{ color: colors.statusActive, fontSize: 16, mt: '2px' }} />
                  : <ErrorOutlineIcon sx={{ color: colors.errorText, fontSize: 16, mt: '2px' }} />}
                <Typography sx={{ ...typo.secondaryText, fontSize: 13, lineHeight: 1.5 }}>
                  <Box component="span" sx={{ fontWeight: 600, color: colors.textPrimary }}>{r.service}</Box>
                  {' — '}
                  {r.success ? (r.message ?? `Cleared ${r.keysRemoved} key(s).`) : (r.error ?? 'Failed.')}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>
      )}

      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ ...typo.sectionTitle, fontSize: 16 }}>Clear all caches?</DialogTitle>
        <DialogContent>
          <Typography sx={{ ...typo.secondaryText, fontSize: 13.5 }}>
            Every tenant on this environment is served from these caches. They refill from the
            database on the next request, so expect a brief slow patch rather than an outage.
          </Typography>
        </DialogContent>
        <DialogActions sx={dialogActionsSx}>
          <Button onClick={() => setConfirmOpen(false)} sx={secondaryButtonSx}>Cancel</Button>
          <Button
            variant="contained"
            sx={primaryButtonSx}
            onClick={() => { setConfirmOpen(false); clear.mutate(); }}
          >
            Clear caches
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default ResetCacheCard;
